import { useGetAttachmentsByDocId } from "@/api/attachment";
import {
  appendNoDuplicateRecordNumberRefine,
  documentSchema,
} from "@/api/document/document-validation";
import {
  getRelativePosition,
  useGetDocument,
  useUpdateDocument,
} from "@/api/document/documents";
import * as AF from "@/components/form-items/AbstractForm";
import { TwoDimensionalInput } from "@/components/form-items/DimensionsInput";
import { ModifiableList } from "@/components/form-items/ModifiableList";
import LinksManagement from "@/components/LinksManagement";
import AddFileModal from "@/components/modal/AddFileModal";
import { MaskableSeparator as Separator } from "@/components/form-items/MaskableSeparator";
import { useAuth } from "@/context/auth/AuthContext";
import { toast } from "@/hooks/use-toast";
import { useDashboardParams } from "@/hooks/useDashboardParams";
import { getMainLanguage } from "@/i18n";
import { FormLayout } from "@/layout/FormLayout";
import { formatFormRegistrationReference } from "@/lib/form-registration-header";
import { computeFieldDocumentSummary } from "@/lib/computeFields";
import { useVisibleT } from "@/lib/cms/cms-new-i18n";
import { Document } from "@/lib/types";
import { standardSchemaResolver } from "@hookform/resolvers/standard-schema";
import { useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useParams, useSearchParams } from "react-router-dom";

const schema = appendNoDuplicateRecordNumberRefine(
  documentSchema.passthrough(),
);

const EditDocument = ({ document }: { document: Document }) => {
  // Rafraîchit le tableau des fichiers après un ajout réussi dans AddFileModal.
  const [refreshFiles, setRefreshFiles] = useState(false);

  const { instanceData } = useAuth();
  const form = useForm({
    resolver: standardSchemaResolver(schema),
    reValidateMode: "onSubmit",
    defaultValues: document,
  });

  const [, setSearchParams] = useSearchParams();

  const { data: attachments } = useGetAttachmentsByDocId(document.id);

  const documentUpdater = useUpdateDocument();

  const { t, i18n } = useTranslation();
  const vt = useVisibleT();

  const handleResume = (data: any) => {
    return data
      ? computeFieldDocumentSummary(data, { language: getMainLanguage() })
      : "";
  };

  const queryClient = useQueryClient();

  const { relativePosition } = useDashboardParams<Document>({
    statusField: "documentsPublicationStatus",
    resourceName: "document",
    relativePosition: {
      currentId: document.id,
      positionFetcher: getRelativePosition,
    },
  });

  const isModuleSculptureEnabled = useMemo(() => {
    return instanceData?.isModuleSculptureEnabled;
  }, [instanceData?.isModuleSculptureEnabled]);

  const documentsDescriptionNumeroEnregistrement = form.watch(
    "documentsDescriptionNumeroEnregistrement",
  );
  const registrationLabel = t("backoffice.header.registrationNumber.label");
  const registrationReference = useMemo(
    () =>
      formatFormRegistrationReference(documentsDescriptionNumeroEnregistrement, {
        label: registrationLabel,
        locale: i18n.language,
      }),
    [documentsDescriptionNumeroEnregistrement, registrationLabel, i18n.language],
  );

  return (
    <FormLayout
      formKey="document"
      onSave={(status) =>
        form.handleSubmit(
          (values) => {
            documentUpdater.mutate(
              {
                ...values,
                ...(status && { documentsPublicationStatus: status }),
              },
              {
                onSuccess: () => {
                  toast({
                    title: t(
                      status === undefined
                        ? "autoSave"
                        : "recordSavedToastTitle",
                    ),
                    variant: "success",
                    description: t("documentSavedToastDescription"),
                  });
                  form.reset({}, { keepValues: true });
                },
              },
            );
          },
          (invalid) => {
            if (invalid.documentsDescriptionNumeroEnregistrement != undefined)
              setSearchParams({ tab: "description" });
          },
        )()
      }
      invalidateOnChange={() => {
        queryClient.invalidateQueries({
          queryKey: ["documents"],
        });
        queryClient.invalidateQueries({
          queryKey: ["document", document.id],
        });
        queryClient.invalidateQueries({
          queryKey: ["attachment", { docId: document.id }],
        });
      }}
      relativePosition={relativePosition}
      publicationStatus={document.documentsPublicationStatus}
      title={document.documentsDescriptionTitre}
      reference={registrationReference}
      id={document.id}
      type="document"
      picturesData={attachments && { pictures: attachments, type: "document" }}
      form={form}
    >
      <Separator />
      <AF.AbstractForm
        form={form}
        formKey="document"
        tabs={
          [
            { nonHideable: true,
              value: "description",
              label: t("documentDescription"),
            },
            {
              value: "retranscription",
              label: t("retranscription"),
            },
            {
              value: "sources",
              label: t("sources"),
            },
            {
              value: "files",
              label: t("fichiers"),
            },
            {
              value: "links",
              label: t("linkedEntries"),
            },
          ] as const
        }
      >
        <AF.Tab value="description">
          <AF.Col>
            <AF.Row className="items-center [&>*]:min-w-0">
              <div className="w-full min-w-0">
                <AF.Text
                  name="documentsDescriptionNumeroEnregistrement"
                  nonMaskable
                />
              </div>
              <AF.Checkbox name="documentsDescriptionCopies" align={true} />
            </AF.Row>
            <ModifiableList
              name="documentsDescriptionType"
              listKey="liste-types-document"
            />
            <AF.Row className="items-start gap-6 [&>*]:min-w-0">
              <div className="w-1/2 min-w-0">
                <AF.Text name="documentsDescriptionClassement" />
              </div>
              <div className="w-1/2 min-w-0">
                <AF.Text name="documentsDescriptionRangement" />
              </div>
            </AF.Row>
            <ModifiableList
              name="documentsDescriptionLocalisation"
              listKey="liste-bibliotheques-archives"
            />
            <div className="w-[calc(50%-12px)] min-w-0">
              <AF.Text name="documentsDescriptionNombreExemplairesArchives" />
            </div>
            <AF.Text
              name="documentsDescriptionTitre"
              nonMaskable
              expandOnOverflow
              className="[field-sizing:content] min-h-9 resize-none"
            />
            <ModifiableList
              name="documentsDescriptionLangueDOrigine"
              listKey="liste-langues"
              label={vt(
                "documentsDescriptionLangueDOrigine",
                "Langue d'origine",
              )}
            />
            <AF.Row className="items-start [&>*]:min-w-0">
              <div className="w-[calc(50%-12px)]">
                <AF.Text name="documentsDescriptionDate" />
              </div>
              <div className="min-w-0 flex-1">
                <ModifiableList
                  name="documentsDescriptionTheme"
                  listKey="liste-themes"
                />
              </div>
            </AF.Row>
            <TwoDimensionalInput parentName="documentsDescriptionDimensions" />
            <ModifiableList
              name="documentsDescriptionTechnique"
              listKey="liste-techniques-document"
            />
            <AF.Text name="documentsDescriptionLieuDeCreation" />
            <ModifiableList
              name="documentsDescriptionPhotographe"
              listKey="liste-photographes"
            />
            <ModifiableList
              name="documentsDescriptionCopyright"
              listKey="liste-credits"
            />
            <AF.Text
              className="w-16"
              name="documentsDescriptionNombreFeuillets"
            />
          </AF.Col>
          <AF.Col>
            <AF.RichText
              name="documentsDescriptionLegendeRedigee"
              computeAction={handleResume}
            />
            <AF.RichText name="documentsDescriptionNotes" />
            <AF.Row>
              <AF.Checkbox name="documentsFichierPhotoBasseDefinitionUniquement" />
              <AF.Checkbox name="documentsFichierPhotoScanAFaire" />
            </AF.Row>
            <AF.Row>
              <AF.Checkbox name="documentsFichierPhotoOkWeb" />
              <AF.Checkbox name="documentsFichierPhotoHdOk" />
            </AF.Row>
            <Separator />
            <AF.Row>
              <AF.Checkbox name="documentsValidationPublicationNumerisee" />
              <AF.Checkbox name="documentsValidationPdfEtOcrOk" />
            </AF.Row>
            <Separator />

            <AF.RadioGroup
              items={[
                { value: "documentsAcquisitionDocumentAAcquerir" },
                { value: "documentsAcquisitionDocumentAcquis" },
              ]}
            />

            <AF.Row>
              <AF.Text name="documentsAcquisitionDateAcquisition" />
              <AF.Text name="documentsAcquisitionVendeur" />
            </AF.Row>
            <AF.RichText name="documentsAcquisitionNotes" />
            <AF.Text name="documentsMouvementsDepot" />
            <AF.RichText name="documentsMouvementsNotesDepot" />
            <AF.LegacySelectCurrency name="documentsMouvementsValeurAssurance" />
          </AF.Col>
        </AF.Tab>
        <AF.Tab value="retranscription">
          <AF.RichText name="documentsDescriptionRetranscription" />
        </AF.Tab>
        <AF.Tab value="sources">
          <AF.RichText name="sources" />
        </AF.Tab>
        <AF.Tab
          isForm={false}
          value="files"
          headerExtras={
            <AddFileModal
              id={document.id}
              onClose={() => {
                setRefreshFiles(true);
              }}
            />
          }
        >
          <AF.OthersFilesTable
            docId={document.id}
            name="diversfiles"
            refreshFiles={refreshFiles}
            setRefreshFiles={setRefreshFiles}
          />
        </AF.Tab>
        <AF.Tab value="links">
          <LinksManagement
            id={document.id}
            type="document"
            configuration={{
              fields: [
                {
                  name: "links",
                  type: "links",
                  label: "Liens",
                  links: [
                    {
                      name: "relatedArtwork",
                      label: t("entryartworks"),
                      type: "link-simple",
                    },
                    {
                      name: "relatedSculptureEditions",
                      label: t("entrysculptureeditions"),
                      type: "link-simple",
                      hidden: !isModuleSculptureEnabled,
                    },
                    {
                      name: "relatedMultipleprint",
                      label: t("entrymultipleprints"),
                      type: "link-simple",
                    },
                    {
                      name: "relatedMultipleSample",
                      label: t("entrymultiplesamples"),
                      type: "link-simple",
                    },
                    {
                      name: "relatedExhibition",
                      label: t("entryexhibitions"),
                      type: "link-simple",
                    },
                    {
                      name: "documentInBibliography",
                      label: t("entrybibliographies"),
                      type: "link-complex",
                      fields: [
                        {
                          name: "bibliography.bibliographieDescriptionNumeroBibliographie",
                          label: t(
                            "bibliographieDescriptionNumeroBibliographie",
                          ),
                          type: "text",
                          className: "w-full",
                          disabled: true,
                        },
                        {
                          name: "bibliography.bibliographieDescriptionResumeLong",
                          label: t("bibliographieDescriptionResumeLong"),
                          type: "richtext",
                          className: "w-full",
                          disabled: true,
                        },
                        {
                          name: "documentsDansBibliographieDescriptionReproduitPage",
                          type: "text",
                          label: t(
                            "documentsDansBibliographieDescriptionReproduitPage",
                          ),
                          className: "w-full",
                        },
                        {
                          name: "documentsDansBibliographieDescriptionCitePage",
                          type: "text",
                          label: t(
                            "documentsDansBibliographieDescriptionCitePage",
                          ),
                          className: "w-full",
                        },
                        {
                          name: "documentsDansBibliographieDescriptionPhotographe",
                          type: "text",
                          label: t(
                            "documentsDansBibliographieDescriptionPhotographe",
                          ),
                          className: "w-full",
                        },
                        {
                          name: "documentsDansBibliographieDescriptionReproductionCouleur",
                          type: "checkbox",
                          label: t(
                            "documentsDansBibliographieDescriptionReproductionCouleur",
                          ),
                          className: "w-full",
                        },
                        {
                          name: "documentsDansBibliographieDescriptionNonPagine",
                          type: "checkbox",
                          label: t(
                            "documentsDansBibliographieDescriptionNonPagine",
                          ),
                          className: "w-full",
                        },
                      ],
                    },
                    {
                      name: "relatedAuction",
                      label: t("entryauctions"),
                      type: "link-simple",
                    },
                    {
                      name: "relatedAudiovisual",
                      label: t("entryaudiovisuals"),
                      type: "link-simple",
                    },
                    {
                      name: "relatedWriting",
                      label: t("entrywritings"),
                      type: "link-simple",
                    },
                    {
                      name: "relatedDocument",
                      label: t("entrydocuments"),
                      type: "link-simple",
                    },
                  ],
                },
              ],
            }}
            data={document}
          />
        </AF.Tab>
      </AF.AbstractForm>
    </FormLayout>
  );
};

export const EditDocumentPage = () => {
  const { id } = useParams();

  const { data, isLoading } = useGetDocument(id);

  return (
    <>
      {data && <EditDocument key={data.id} document={data} />}
      {!data && !isLoading && <h2>noDocumentLabel</h2>}
    </>
  );
};
